import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Dices, Waypoints, Target, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";

const TOOLS = [
  {
    slug: "goal-planner",
    title: "Goal-Based Planner",
    tag: "Live",
    tagLive: true,
    body: "A working advisor's cockpit. Define client goals, priorities, and life events — see whether the plan holds, and export a PDF report.",
    icon: <Target size={20} strokeWidth={1.5} />,
  },
  {
    slug: "pension-planning",
    title: "Pension Planning",
    tag: "Live",
    tagLive: true,
    body: "Starting from your MyINPS tasso di sostituzione, compute the retirement gap and the monthly savings needed to close it. Italian workers, TFR-aware.",
    icon: <ShieldCheck size={20} strokeWidth={1.5} />,
  },
  {
    slug: "compound-interest",
    title: "Compound Interest",
    tag: "Live",
    tagLive: true,
    body: "Model long-horizon growth of an initial capital plus recurring contributions. Move the sliders, watch the curve breathe.",
    icon: <TrendingUp size={20} strokeWidth={1.5} />,
  },
  {
    slug: "monte-carlo-dcf",
    title: "Monte Carlo DCF",
    tag: "Live",
    tagLive: true,
    body: "Simulates thousands of EPS paths under mean-reverting growth with occasional shocks, discounts them, and shows the distribution of intrinsic prices that assumption set could justify.",
    icon: <Dices size={20} strokeWidth={1.5} />,
  },
  {
    slug: "is-lm",
    title: "IS–LM Simulator",
    tag: "Live",
    tagLive: true,
    body: "The Hicks–Hansen model made tactile. Slide fiscal and monetary policy; watch the curves and the equilibrium move. Built for macro students who want to feel the model, not just read it.",
    icon: <Waypoints size={20} strokeWidth={1.5} />,
  },
];

export default function Tools() {
  return (
    <main data-testid="tools-page" className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <Seo
        title="Financial Tools"
        description="A small workbench of financial tools used daily — goal-based planning, pension gap analysis, compound interest, Monte Carlo DCF, IS–LM simulator."
        path="/tools"
      />
      <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-4">
        Tools
      </div>
      <h1 className="font-serif text-5xl md:text-7xl font-light leading-[1.02] tracking-tighter">
        A small workbench,
        <br />
        <span className="italic text-primary">used daily.</span>
      </h1>
      <p className="mt-8 max-w-2xl text-lg text-foreground/70 leading-relaxed">
        Financial tools I actually use — shipped one at a time, kept honest by
        the writing that surrounds them.
      </p>

      <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-6">
        {TOOLS.map((t) => (
          <Link
            key={t.slug}
            to={`/tools/${t.slug}`}
            data-testid={`tool-card-${t.slug}`}
            className="bento-card group relative flex flex-col justify-between border border-border rounded-sm p-8 md:p-10 bg-background hover:border-primary min-h-[280px]"
          >
            <div className="flex items-center justify-between">
              <div className="w-11 h-11 rounded-full border border-border flex items-center justify-center text-primary">
                {t.icon}
              </div>
              <span
                className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-border ${
                  t.tagLive ? "bg-primary text-primary-foreground border-primary" : "text-foreground/70"
                }`}
              >
                {t.tag}
              </span>
            </div>
            <div className="mt-10">
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">
                {t.title}
              </h3>
              <p className="mt-4 text-foreground/70 leading-relaxed text-base max-w-md">
                {t.body}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary">
                Open the tool <ArrowUpRight size={14} strokeWidth={1.5} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}
